import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  alpha,
  useTheme,
  Skeleton
} from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

const DashboardCard = ({ 
  title, 
  value, 
  icon, 
  color = 'primary',
  trend,
  subtitle,
  onClick,
  loading = false
}) => {
  const theme = useTheme();
  
  const colorMap = {
    primary: theme.palette.primary.main,
    success: theme.palette.success.main,
    warning: theme.palette.warning.main,
    error: theme.palette.error.main,
    info: theme.palette.info.main
  };

  const selectedColor = colorMap[color] || colorMap.primary;

  const formattedValue = (value ?? 0).toLocaleString();
  const isTrendPositive = typeof trend === 'number' && trend > 0;
  const isTrendNegative = typeof trend === 'number' && trend < 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        height: '100%',
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(selectedColor, 0.08)} 0%, ${alpha(selectedColor, 0.03)} 100%)`,
        border: `1px solid ${alpha(selectedColor, 0.18)}`,
        transition: 'all 0.25s ease',
        cursor: onClick ? 'pointer' : 'default',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          transform: onClick ? 'translateY(-4px)' : 'none',
          boxShadow: `0 10px 24px ${alpha(selectedColor, 0.18)}`,
          borderColor: alpha(selectedColor, 0.35)
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${selectedColor} 0%, ${alpha(selectedColor, 0.7)} 100%)`,
          borderRadius: '3px 3px 0 0'
        }
      }}
    >
      <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          {loading ? (
            <Skeleton variant="text" width={120} height={16} />
          ) : (
            <Typography 
              variant="overline" 
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: '0.6px' }}
            >
              {title}
            </Typography>
          )}

          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              background: alpha(selectedColor, 0.12),
              color: selectedColor,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: `1px solid ${alpha(selectedColor, 0.2)}`
            }}
          >
            {loading ? (
              <Skeleton variant="circular" width={24} height={24} />
            ) : (
              icon
            )}
          </Box>
        </Box>

        {/* Value */}
        {loading ? (
          <Skeleton variant="text" width="60%" height={42} sx={{ mb: 1 }} />
        ) : (
          <Typography 
            variant="h4" 
            component="div"
            sx={{ fontWeight: 800, color: 'text.primary', mb: 0.75, lineHeight: 1.2 }}
          >
            {formattedValue}
          </Typography>
        )}

        {/* Subtitle */}
        {subtitle && (
          loading ? (
            <Skeleton variant="text" width="80%" height={16} />
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {subtitle}
            </Typography>
          )
        )}

        {/* Trend */}
        {typeof trend === 'number' && !loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 'auto', pt: 1 }}>
            <Typography 
              variant="caption" 
              sx={{ 
                color: isTrendPositive ? 'success.main' : isTrendNegative ? 'error.main' : 'text.secondary',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              {isTrendPositive ? <TrendingUp sx={{ fontSize: 16 }} /> : isTrendNegative ? <TrendingDown sx={{ fontSize: 16 }} /> : null}
              {trend > 0 ? '+' : ''}{trend}%
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default DashboardCard; 